import { defineStore } from 'pinia'

export interface MenuItem {
    path: string,
    title: string,
    icon: string,
    position?: number,
    children?: MenuItem[],
}

type PathParams = Record<string, string>

const PARAM_PREFIX = ':'

function matchPath(templatePath: string, path: string): PathParams | null {
    const templateSegments = templatePath.split('/')
    const pathSegments = path.split('/')
    if (templateSegments.length !== pathSegments.length) {
        return null
    }
    const params: PathParams = {}
    for (const [index, templateSegment] of templateSegments.entries()) {
        const pathSegment = pathSegments[index]!
        if (templateSegment.startsWith(PARAM_PREFIX)) {
            if (pathSegment === '') {
                return null
            }
            params[templateSegment] = pathSegment
        }
        else if (templateSegment !== pathSegment) {
            return null
        }
    }
    return params
}

function fillPath(templatePath: string, params: PathParams): string {
    const segments = templatePath
        .split('/')
        .map((segment) => params[segment] ?? segment)
    return segments.join('/')
}

function findSection(items: MenuItem[], path: string): MenuItem | null {
    for (const item of items) {
        const params = matchPath(item.path, path)
        if (params) {
            const children = item.children?.map((child) => ({
                ...child,
                path: fillPath(child.path, params),
            }))
            return {
                ...item,
                children,
            }
        }
        const nestedSection = findSection(item.children ?? [], path)
        if (nestedSection) {
            return nestedSection
        }
    }
    return null
}

export const useMenuStore = defineStore('menu', () => {
    const menu = ref<MenuItem[]>([])
    let pending: Promise<unknown> | null = null

    async function fetchMenu(force = false): Promise<void> {
        if (!force && menu.value.length > 0) {
            return
        }
        if (pending) {
            await pending
            return
        }
        pending = useRequest({
            label: 'fetch menu',
            fetch: () => $fetch<MenuItem[]>('/api/me/menu'),
            evaluate: (data: any) => Array.isArray(data) ? [...data] : [],
            store: (data) => { menu.value = data },
        })
        try {
            await pending
        }
        finally {
            pending = null
        }
    }

    function resolveSection(path: string): MenuItem | null {
        return findSection(menu.value, path)
    }

    return {
        menu,
        fetchMenu,
        resolveSection,
    }
})
